/**
 * Supported languages: the one list the app picks a language from.
 * Mirrors backend/utils/locales.py. Only `enabled` languages are offered to users; the others are
 * listed so their catalogs can be added and tested before they ship.
 */

export type LanguageCode = 'en' | 'tr' | 'de' | 'es' | 'fr' | 'ru' | 'uk' | 'ar' | 'fa';

export interface LanguageInfo {
  code: LanguageCode;
  /** Shown in the language picker, in the language itself */
  nativeName: string;
  /** BCP 47 locale for Intl date/number formatting */
  intlLocale: string;
  dir: 'ltr' | 'rtl';
  enabled: boolean;
}

export const LANGUAGES: LanguageInfo[] = [
  { code: 'en', nativeName: 'English', intlLocale: 'en-US', dir: 'ltr', enabled: true },
  { code: 'tr', nativeName: 'Türkçe', intlLocale: 'tr-TR', dir: 'ltr', enabled: true },
  { code: 'de', nativeName: 'Deutsch', intlLocale: 'de-DE', dir: 'ltr', enabled: true },
  { code: 'es', nativeName: 'Español', intlLocale: 'es-ES', dir: 'ltr', enabled: true },
  { code: 'fr', nativeName: 'Français', intlLocale: 'fr-FR', dir: 'ltr', enabled: true },
  { code: 'ru', nativeName: 'Русский', intlLocale: 'ru-RU', dir: 'ltr', enabled: true },
  { code: 'uk', nativeName: 'Українська', intlLocale: 'uk-UA', dir: 'ltr', enabled: true },
  { code: 'ar', nativeName: 'العربية', intlLocale: 'ar', dir: 'rtl', enabled: true },
  { code: 'fa', nativeName: 'فارسی', intlLocale: 'fa-IR', dir: 'rtl', enabled: true },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const ENABLED_LANGUAGES = LANGUAGES.filter((lang) => lang.enabled);

export const isEnabledLanguage = (value: unknown): value is LanguageCode =>
  typeof value === 'string' && ENABLED_LANGUAGES.some((lang) => lang.code === value);

/** 'tr-TR' / 'TR' -> 'tr'; null when it is not an enabled language. */
export const matchLanguage = (value: string | null | undefined): LanguageCode | null => {
  const code = (value || '').trim().toLowerCase().replace('_', '-').split('-')[0];
  return isEnabledLanguage(code) ? code : null;
};

export const languageInfo = (code: string | null | undefined): LanguageInfo =>
  LANGUAGES.find((lang) => lang.code === code) ?? LANGUAGES[0];
